import { InvokeCommand, LambdaClient } from "@aws-sdk/client-lambda";
import { fetchAuthSession } from "aws-amplify/auth";
import { Cache } from "aws-amplify/utils";

// env
const {
  REACT_APP_REGION,
  REACT_APP_SIGN_ASSETS_CDN_LAMBDA_NAME,
} = process.env;


/**
 * Checks if a cached signed asset URL is still valid.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.url - The asset URL to check in cache
 * @returns {Promise<string|null>} The cached signed URL if valid and not expired, otherwise null
 *
 * @example
 * const cachedUrl = await isCachedValid({ url: 'assets/invoice.pdf' });
 * // Returns: 'https://cdn.example.com/...' or null
 */
const isCachedValid = async ({ url }) => {
  try {
    const cached = await Cache.getItem(url);

    // sanity check
    if (!cached) return null;

    // check date
    const now = Math.floor(new Date().getTime() / 1000);
    const expired = Math.floor(new Date(cached.expires_at).getTime() / 1000);

    // cache hit
    if (now < expired) {
      console.log('services.helpers#sign.asset.url@isCachedValid', 'cache hit');
      return cached.signed_url;
    }

    // non-cache
    console.log('services.helpers#sign.asset.url@isCachedValid', 'cache missed');
    return null;
    
  } catch (err) {
    console.warn('services.helpers#sign.asset.url@isCachedValid', err);
    return null;
  }
}

/**
 * Invokes the Lambda function to generate a signed URL for a CDN asset.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.url - The asset path to sign (e.g., 's3://bucket/path/to/asset.pdf')
 * @param {number} params.expires_at - The time-to-live in seconds for the signed URL
 * @returns {Promise<Object>} Response object containing `signed_url` (the CloudFront signed URL) and `expires_at` (ISO timestamp)
 * @throws {Error} If the Lambda invocation fails or returns an error message
 *
 * @example
 * const result = await sign({ url: 's3://bucket/invoice.pdf', expires_at: 3600 });
 * // Returns: { signed_url: 'https://cdn.example.com/...', expires_at: '2026-07-29T11:06:26Z' }
 */
const sign = async ({ url, expires_at }) => {
  // credentials
  const { credentials } = await fetchAuthSession();

  // clients
  const lambda = new LambdaClient({
    region: REACT_APP_REGION,
    credentials: credentials,
  })

  const command = new InvokeCommand({
    FunctionName: REACT_APP_SIGN_ASSETS_CDN_LAMBDA_NAME,
    Payload: JSON.stringify({
      path: url,
      cache_seconds: expires_at,
    })
  });

  // execute
  const res = await lambda.send(command);
  const data = res.Payload.transformToString();
  const json = JSON.parse(data);
  console.log('services.helpers#sign.asset.url@signAssetUrl', 'json', json);

  // sanity check
  if (json && json.errorMessage) throw new Error(json.errorMessage);
  return json;
}

/**
 * Retrieves a signed URL for a CDN asset, using cache when available.
 *
 * Checks if a signed URL for the asset is cached and not expired. If found, returns the cached URL immediately.
 * Otherwise, invokes the Lambda signing function, caches the result, and returns the signed URL.
 * Signed URLs allow temporary access to private CDN assets without authentication headers.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.url - The asset path to sign (e.g., 's3://bucket/invoices/12345.pdf')
 * @param {number} [params.expires_at=3600] - The time-to-live in seconds for the signed URL (default: 1 hour)
 * @returns {Promise<string>} The CloudFront signed URL that grants temporary access to the asset
 * @throws {Error} If fetching credentials fails or Lambda invocation returns an error
 *
 * @example
 * const signedUrl = await getAssetSignedUrl({ 
 *   url: 's3://bucket/invoices/INV-001.pdf',
 *   expires_at: 7200 // Valid for 2 hours
 * });
 * // Returns: 'https://d1234.cloudfront.net/invoices/INV-001.pdf?Policy=...'
 */
export const getAssetSignedUrl = async ({ url, expires_at = 3600 }) => {
  try {

    // check for cache
    const cached = await isCachedValid({url});

    // sanity check
    if (cached) return cached;

    // cache miss or first time, the need to sign and cache it
    const signed = await sign({ url, expires_at });
    await Cache.setItem(url, {
      signed_url: signed.signed_url,
      expires_at: new Date(signed.expires_at).toISOString(),
    }, {
      expires: new Date(signed.expires_at).getTime(),
    });

    return signed.signed_url;

  } catch (err) {
    console.error('services.helpers#sign.asset.url@signAssetUrl', err);
    throw err;
  }
}
